import { useState, useEffect } from 'react';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/BusinessRounded';

import { useGetCurrentUserQuery } from 'utils/wemble-api';
import { useSetAuth } from 'hooks';
import { haptic } from '../../utils/haptics';
import { useMobilePageTitle, useMobileBackPath } from '../../contexts/MobilePageTitleContext';
import ArrowBackIosRoundedIcon from '@material-ui/icons/ArrowBackIosRounded';

import wIcon from '../../assets/wemble.svg';
import dashboardIcon from '../../assets/icons/home.svg';
import projectsIcon from '../../assets/icons/projects.svg';
import competenceIcon from '../../assets/icons/competences.svg';
import settingsIcon from '../../assets/icons/settings.svg';
import usersIcon from '../../assets/icons/users.svg';
import logoutIcon from '../../assets/icons/logout.svg';
import dotsIcon from '../../assets/icons/dots.svg';
import organization from '../../utils/organization';

const ACTIVE_ICON_FILTER = 'brightness(0)';

// Old iOS app (<= v5.0) adds bottom safe area via native contentInset,
// so CSS should NOT also add env(safe-area-inset-bottom) or it doubles up.
// v5.0.1+ uses CSS-only safe areas (contentInsetAdjustmentBehavior = .never).
const isOldIosApp = (() => {
  const match = /Wemble\(iOS\)\s+([\d.]+)/.exec(navigator.userAgent);
  if (!match) return false;
  const parts = match[1].split('.').map(Number);
  if (parts[0] < 5) return true;
  if (parts[0] === 5 && (parts[1] || 0) === 0 && (parts[2] || 0) < 1) return true;
  return false;
})();

const useStyles = makeStyles(() => ({
  topBar: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1040,
    backgroundColor: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 16px',
    paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
    paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
    paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))',
    borderBottom: '1px solid #F0F0F0',
  },
  topBar__title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#222',
    margin: 0,
  },
  topBar__backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    marginLeft: -4,
    color: '#222',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    '& svg': {
      fontSize: 20,
    },
  },
  topBar__logo: {
    height: 24,
  },

  bottomBar: {
    position: 'fixed',
    bottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
    left: 12,
    right: 12,
    zIndex: 1040,
    backgroundColor: 'rgba(255, 255, 255, 0.88)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    borderRadius: 28,
    boxShadow: '0 2px 20px rgba(0, 0, 0, 0.1), 0 0 0 0.5px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    alignItems: 'center',
    padding: '6px 24px',
    height: 58,
  },

  tabItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none !important',
    color: '#999 !important',
    fontSize: 9,
    fontWeight: 500,
    gap: 2,
    padding: '0 16px',
    margin: '0 -16px',
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    userSelect: 'none',
    WebkitUserDrag: 'none',
    transition: 'transform 0.15s ease',
    '&:active': {
      transform: 'scale(0.85)',
    },
    '& img': {
      width: 22,
      height: 22,
      opacity: 0.45,
      pointerEvents: 'none',
    },
    '& svg': {
      width: 22,
      height: 22,
      opacity: 0.45,
      color: '#999',
      pointerEvents: 'none',
    },
    '&.active': {
      color: '#222 !important',
      '& img': {
        opacity: 1,
        filter: ACTIVE_ICON_FILTER,
      },
      '& svg': {
        opacity: 1,
        color: '#222',
      },
    },
  },
  tabLabel: {
    lineHeight: 1,
    marginTop: 2,
  },

  moreButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
    fontSize: 9,
    fontWeight: 500,
    gap: 2,
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    userSelect: 'none',
    background: 'none',
    border: 'none',
    padding: '0 16px',
    margin: '0 -16px',
    cursor: 'pointer',
    transition: 'transform 0.15s ease',
    '&:active': {
      transform: 'scale(0.85)',
    },
    '& img': {
      width: 22,
      height: 22,
      opacity: 0.45,
      transform: 'rotate(90deg)',
    },
  },
  moreButtonActive: {
    color: '#222',
    '& img': {
      opacity: 1,
      filter: ACTIVE_ICON_FILTER,
    },
  },

  popoverBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1041,
  },
  popover: {
    position: 'fixed',
    bottom: 'calc(68px + max(12px, env(safe-area-inset-bottom, 12px)))',
    right: 16,
    zIndex: 1042,
    backgroundColor: 'white',
    borderRadius: 14,
    boxShadow: '0 8px 40px rgba(0, 0, 0, 0.15), 0 0 0 0.5px rgba(0, 0, 0, 0.05)',
    padding: '6px 0',
    minWidth: 180,
    animation: '$popoverIn 0.15s ease-out',
  },
  popoverItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 18px',
    textDecoration: 'none !important',
    color: '#333 !important',
    fontSize: 15,
    fontWeight: 500,
    WebkitTapHighlightColor: 'transparent',
    '&:active': {
      backgroundColor: '#F0F0F0',
    },
    '& img': {
      width: 20,
      height: 20,
      opacity: 0.6,
    },
    '& svg': {
      width: 20,
      height: 20,
      opacity: 0.6,
    },
  },
  popoverDivider: {
    height: 1,
    backgroundColor: '#F0F0F0',
    margin: '4px 0',
  },
  '@keyframes popoverIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(8px) scale(0.97)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
}));

const MobileNavigation = () => {
  const classes = useStyles();
  const [moreOpen, setMoreOpen] = useState(false);
  const setAuth = useSetAuth();
  const history = useHistory();
  const { pathname } = useLocation();
  const { data: user } = useGetCurrentUserQuery();

  const organizationConfig = organization(user?.company);
  const pageTitle = useMobilePageTitle() || 'Wemble';
  const backPath = useMobileBackPath();

  // Add body class for old iOS app CSS targeting
  useEffect(() => {
    if (isOldIosApp) document.body.classList.add('old-ios-app');
  }, []);

  // Close popover on navigation
  useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  const hasManageUsers = !!(user && (user.admin || user.administratorPriviliges) && !user.companyAdmin);
  const hasAdmin = !!user?.companyAdmin;

  // Highlight "More" button when a child route is active
  const moreRouteActive = pathname.startsWith('/users') || pathname.startsWith('/groups') || pathname.startsWith('/offices');

  // Count visible tabs to adjust bar layout (fewer items = clustering with gap)
  // Home + Settings + More are always visible (3), Projects + Competences are conditional
  const tabCount = 3 + (!organizationConfig.disableProjects ? 1 : 0) + (!organizationConfig.disableCompetences ? 1 : 0);

  const onTabClick = () => haptic('light');

  return (
    <>
      {/* Top bar with page title and logo */}
      <div className={classes.topBar}>
        {backPath ? (
          <button className={classes.topBar__backButton} onClick={() => { haptic('light'); history.push(backPath); }}>
            <ArrowBackIosRoundedIcon />
            <span className={classes.topBar__title}>{pageTitle}</span>
          </button>
        ) : (
          <span className={classes.topBar__title}>{pageTitle}</span>
        )}
        <img src={wIcon} className={classes.topBar__logo} alt="Wemble" />
      </div>

      {/* Floating bottom tab bar */}
      <nav className={classes.bottomBar} style={{
        padding: `6px ${tabCount >= 5 ? 24 : 32}px`,
        justifyContent: tabCount >= 5 ? 'space-between' : 'flex-start',
        gap: tabCount >= 5 ? undefined : 42,
      }}>
        {user && (
          <NavLink
            to="/home"
            className={classes.tabItem}
            activeClassName="active"
            isActive={(_, location) => location.pathname === '/home' || location.pathname === '/'}
            onClick={onTabClick}
          >
            <img src={dashboardIcon} alt="" />
            <span className={classes.tabLabel}>Home</span>
          </NavLink>
        )}

        {user && !organizationConfig.disableCompetences && (
          <NavLink
            to="/competences"
            className={classes.tabItem}
            activeClassName="active"
            isActive={(_, location) => location.pathname.startsWith('/competences')}
            onClick={onTabClick}
            style={{ margin: '0 -20px' }}
          >
            <img src={competenceIcon} alt="" />
            <span className={classes.tabLabel}>Competencies</span>
          </NavLink>
        )}

        {user && !organizationConfig.disableProjects && (
          <NavLink
            to="/projects"
            className={classes.tabItem}
            activeClassName="active"
            isActive={(_, location) => location.pathname.startsWith('/projects')}
            onClick={onTabClick}
          >
            <img src={projectsIcon} alt="" />
            <span className={classes.tabLabel}>Projects</span>
          </NavLink>
        )}

        {user && (
          <NavLink
            to="/settings"
            className={classes.tabItem}
            activeClassName="active"
            onClick={onTabClick}
          >
            <img src={settingsIcon} alt="" />
            <span className={classes.tabLabel}>Settings</span>
          </NavLink>
        )}

        <button
          className={`${classes.moreButton} ${(moreOpen || moreRouteActive) ? classes.moreButtonActive : ''}`}
          onClick={() => { haptic('light'); setMoreOpen(!moreOpen); }}
          style={tabCount < 5 ? { marginLeft: 'auto' } : undefined}
        >
          <img src={dotsIcon} alt="" />
          <span className={classes.tabLabel}>More</span>
        </button>
      </nav>

      {/* More popover */}
      {moreOpen && (
        <>
          <div className={classes.popoverBackdrop} onClick={() => setMoreOpen(false)} />
          <div className={classes.popover}>
            {hasManageUsers && (
              <NavLink to="/users" className={classes.popoverItem}>
                <img src={usersIcon} alt="" />
                Manage users
              </NavLink>
            )}
            {hasAdmin && (
              <NavLink to="/groups" className={classes.popoverItem}>
                <BusinessIcon />
                Admin
              </NavLink>
            )}
            {(hasManageUsers || hasAdmin) && <div className={classes.popoverDivider} />}
            <Link
              to="/"
              className={classes.popoverItem}
              onClick={() => setAuth(false)}
            >
              <img src={logoutIcon} alt="" />
              Log out
            </Link>
          </div>
        </>
      )}
    </>
  );
};

export default MobileNavigation;
